import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
    ClipboardList,
    Search,
    Filter,
    Eye,
    X,
    Calendar,
    ChevronLeft,
    ChevronRight,
    AlertCircle,
    Info,
    Loader2,
    User,
    Clock,
    Shield,
    Monitor,
    ArrowRight,
} from "lucide-react";
import AdminLayout from "../components/admin/AdminLayout";
import AdminAuditLogService from "../services/admin/AdminAuditLogService";
import { useAdminPermissions } from "../hooks/useAdminPermissions";
import { useNotification } from "../context/NotificationContext";
import { useNavigate } from "react-router-dom";

// ── Timestamp formatter ──────────────────────────────────
function formatTimestamp(raw) {
    if (!raw) return "–";
    try {
        const d = new Date(raw);
        if (isNaN(d.getTime())) return raw; // fallback: show as-is
        return d.toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
            hour12: true,          // ← AM / PM
        }).replace(",", "").replace(",", " ·"); // "Feb 24 2026 · 3:45 PM"
    } catch {
        return raw;
    }
}

// ── Severity config ───────────────────────────────────────
const SEVERITY_CONFIG = {
    info: { label: "Info", color: "text-gray-600 bg-gray-100 border-gray-200 dark:text-gray-400 dark:bg-gray-800 dark:border-gray-700" },
    update: { label: "Update", color: "text-blue-600 bg-blue-50 border-blue-200 dark:text-blue-400 dark:bg-blue-900/20 dark:border-blue-800" },
    status_change: { label: "Status Change", color: "text-teal-600 bg-teal-50 border-teal-200 dark:text-teal-400 dark:bg-teal-900/20 dark:border-teal-800" },
    critical: { label: "Critical", color: "text-red-600 bg-red-50 border-red-200 dark:text-red-400 dark:bg-red-900/20 dark:border-red-800" },
};

// ── Skeleton ──────────────────────────────────────────────
function AuditLogsSkeleton() {
    return (
        <div className="space-y-4 animate-pulse">
            {/* Filter bar skeleton */}
            <div className="bg-white dark:bg-[#1A1A1A] p-4 rounded-2xl border border-[#E5E7EB] dark:border-[#2A2A2A]">
                <div className="flex flex-wrap gap-3">
                    {[1, 2, 3, 4, 5].map(i => (
                        <div key={i} className="h-10 w-36 bg-gray-200 dark:bg-[#333] rounded-xl" />
                    ))}
                </div>
            </div>
            {/* Table skeleton */}
            <div className="bg-white dark:bg-[#1A1A1A] rounded-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] overflow-hidden">
                {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                    <div key={i} className="flex items-center gap-4 px-6 py-4 border-b border-[#F3F4F6] dark:border-[#2A2A2A]">
                        <div className="h-4 w-28 bg-gray-200 dark:bg-[#333] rounded" />
                        <div className="h-4 w-24 bg-gray-200 dark:bg-[#333] rounded" />
                        <div className="h-4 w-44 bg-gray-200 dark:bg-[#333] rounded flex-1" />
                        <div className="h-6 w-16 bg-gray-200 dark:bg-[#333] rounded-full" />
                        <div className="h-8 w-8 bg-gray-200 dark:bg-[#333] rounded-lg" />
                    </div>
                ))}
            </div>
        </div>
    );
}

// ── Detail Modal ──────────────────────────────────────────
function LogDetailModal({ log, onClose }) {
    if (!log) return null;

    const sev = SEVERITY_CONFIG[log.severity] || SEVERITY_CONFIG.info;

    const renderState = (state, label) => {
        if (!state || typeof state !== "object" || Object.keys(state).length === 0) return null;
        return (
            <div className="space-y-2">
                <h4 className="text-xs font-bold text-[#6B7280] dark:text-[#A0A0A0] uppercase">{label}</h4>
                <div className="bg-[#F9FAFB] dark:bg-[#262626] border border-[#E5E7EB] dark:border-[#333] rounded-xl p-4 space-y-1.5">
                    {Object.entries(state).map(([key, value]) => (
                        <div key={key} className="flex items-start gap-2 text-sm">
                            <span className="text-[#6B7280] dark:text-[#A0A0A0] font-medium min-w-[100px]">{key}:</span>
                            <span className="text-[#111] dark:text-white font-mono text-xs break-all">
                                {typeof value === "object" ? JSON.stringify(value, null, 2) : String(value)}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
            <div
                className="relative bg-white dark:bg-[#1A1A1A] rounded-3xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto border border-[#E5E7EB] dark:border-[#2A2A2A]"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="sticky top-0 bg-white dark:bg-[#1A1A1A] p-6 pb-4 border-b border-[#E5E7EB] dark:border-[#2A2A2A] z-10">
                    <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-bold text-[#111] dark:text-white font-poppins truncate">
                                {log.action_display}
                            </h3>
                            <div className="flex items-center gap-3 mt-2">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border ${sev.color}`}>
                                    {sev.label}
                                </span>
                                <span className="text-xs text-[#6B7280] dark:text-[#A0A0A0]">{formatTimestamp(log.timestamp)}</span>
                            </div>
                        </div>
                        <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-[#262626] rounded-xl transition-colors">
                            <X size={18} className="text-[#6B7280]" />
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="p-6 space-y-6">
                    {/* Admin Info */}
                    <div className="flex items-center gap-3 p-3 bg-[#F9FAFB] dark:bg-[#262626] rounded-xl">
                        <div className="w-9 h-9 bg-purple-100 dark:bg-purple-900/20 rounded-full flex items-center justify-center">
                            <User size={16} className="text-purple-600" />
                        </div>
                        <div>
                            <p className="text-sm font-bold text-[#111] dark:text-white">{log.admin_name}</p>
                            <p className="text-xs text-[#6B7280] dark:text-[#A0A0A0]">Administrator</p>
                        </div>
                    </div>

                    {/* Entity Info */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1">
                            <span className="text-xs font-bold text-[#6B7280] dark:text-[#A0A0A0] uppercase">Entity Type</span>
                            <p className="text-sm text-[#111] dark:text-white font-medium capitalize">{log.entity_type?.replace("_", " ")}</p>
                        </div>
                        <div className="space-y-1">
                            <span className="text-xs font-bold text-[#6B7280] dark:text-[#A0A0A0] uppercase">Entity ID</span>
                            <p className="text-xs text-[#111] dark:text-white font-mono break-all">{log.entity_id}</p>
                        </div>
                        <div className="space-y-1">
                            <span className="text-xs font-bold text-[#6B7280] dark:text-[#A0A0A0] uppercase">Action</span>
                            <p className="text-sm text-[#111] dark:text-white font-medium capitalize">{log.action?.replace("_", " ")}</p>
                        </div>
                        <div className="space-y-1">
                            <span className="text-xs font-bold text-[#6B7280] dark:text-[#A0A0A0] uppercase">Timestamp</span>
                            <p className="text-sm text-[#111] dark:text-white">{formatTimestamp(log.timestamp)}</p>
                        </div>
                    </div>

                    {/* Change Details */}
                    {(log.before_state || log.after_state) && (
                        <div className="space-y-4">
                            <h4 className="text-sm font-bold text-[#111] dark:text-white flex items-center gap-2">
                                <ArrowRight size={14} className="text-purple-500" />
                                Change Details
                            </h4>
                            {log.before_state && log.after_state ? (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {renderState(log.before_state, "Before")}
                                    {renderState(log.after_state, "After")}
                                </div>
                            ) : (
                                <>
                                    {renderState(log.before_state, "Before")}
                                    {renderState(log.after_state, "After")}
                                </>
                            )}
                        </div>
                    )}

                    {/* Metadata */}
                    {(log.ip_address || log.user_agent) && (
                        <div className="space-y-3 pt-4 border-t border-[#E5E7EB] dark:border-[#2A2A2A]">
                            <h4 className="text-xs font-bold text-[#6B7280] dark:text-[#A0A0A0] uppercase">Metadata</h4>
                            {log.ip_address && (
                                <div className="flex items-center gap-2 text-sm">
                                    <Shield size={14} className="text-[#9CA3AF]" />
                                    <span className="text-[#6B7280] dark:text-[#A0A0A0]">IP:</span>
                                    <span className="text-[#111] dark:text-white font-mono text-xs">{log.ip_address}</span>
                                </div>
                            )}
                            {log.user_agent && (
                                <div className="flex items-start gap-2 text-sm">
                                    <Monitor size={14} className="text-[#9CA3AF] flex-shrink-0 mt-0.5" />
                                    <span className="text-[#6B7280] dark:text-[#A0A0A0] flex-shrink-0">Device:</span>
                                    <span className="text-[#111] dark:text-white text-xs break-all">{log.user_agent}</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}


// ── Main Page ─────────────────────────────────────────────
export default function AuditLogsPage() {
    const navigate = useNavigate();
    const { showNotification } = useNotification();
    const { hasPermission } = useAdminPermissions();
    const [logs, setLogs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });

    // Filters
    const [filterOptions, setFilterOptions] = useState({ admins: [], entity_types: [], actions: [] });
    const [filters, setFilters] = useState({
        admin_id: "",
        action: "",
        entity_type: "",
        date_from: "",
        date_to: "",
        search: "",
    });
    const [showFilters, setShowFilters] = useState(false);

    // Detail modal
    const [selectedLog, setSelectedLog] = useState(null);
    const [isLoadingDetail, setIsLoadingDetail] = useState(false);

    const hasActiveFilters = useMemo(() =>
        Object.values(filters).some(v => v !== ""), [filters]);

    const fetchLogs = useCallback(async () => {
        setIsLoading(true);
        try {
            const params = { page: pagination.page, limit: pagination.limit };
            Object.entries(filters).forEach(([key, val]) => {
                if (val) params[key] = val;
            });
            const result = await AdminAuditLogService.getAll(params);
            if (result.status === "success") {
                setLogs(result.data || []);
                setPagination(prev => ({ ...prev, ...result.pagination }));
            }
        } catch (error) {
            console.error("Failed to fetch audit logs", error);
        } finally {
            setIsLoading(false);
        }
    }, [pagination.page, pagination.limit, filters]);

    const fetchFilterOptions = async () => {
        try {
            const result = await AdminAuditLogService.getFilterOptions();
            if (result.status === "success") {
                setFilterOptions(result.data);
            }
        } catch (error) {
            console.error("Failed to fetch filter options", error);
        }
    };

    useEffect(() => {
        fetchFilterOptions();
    }, []);

    useEffect(() => {
        if (!hasPermission("super_admin")) {
            showNotification({
                type: "failed",
                title: "Access Restricted",
                message: "You do not have permission to view audit logs.",
            });
            navigate("/admin/dashboard");
        }
    }, [hasPermission, navigate, showNotification]);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const clearFilters = () => {
        setFilters({ admin_id: "", action: "", entity_type: "", date_from: "", date_to: "", search: "" });
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const openDetail = async (logId) => {
        setIsLoadingDetail(true);
        try {
            const result = await AdminAuditLogService.getDetail(logId);
            if (result.status === "success") {
                setSelectedLog(result.data);
            }
        } catch (error) {
            console.error("Failed to fetch log detail", error);
        } finally {
            setIsLoadingDetail(false);
        }
    };

    const selectClass = "px-3 py-2.5 bg-[#F9FAFB] dark:bg-[#262626] border border-[#E5E7EB] dark:border-[#333] rounded-xl text-sm text-[#111] dark:text-white focus:ring-2 focus:ring-[#62397f] outline-none transition-all appearance-none cursor-pointer";

    return (
        <AdminLayout>
            <div className="p-4 lg:p-8 max-w-[1600px] mx-auto space-y-6">
                {/* Page Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-[#111111] dark:text-white font-poppins">
                            Audit Logs
                        </h1>
                        <p className="text-sm text-[#6B7280] dark:text-[#A0A0A0]">
                            Track all administrative activity in the system
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        {hasActiveFilters && (
                            <button
                                onClick={clearFilters}
                                className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 rounded-xl transition-colors"
                            >
                                <X size={16} />
                                Clear Filters
                            </button>
                        )}
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all border ${showFilters
                                ? "bg-[#62397f] text-white border-[#62397f]"
                                : "bg-white dark:bg-[#1A1A1A] text-[#111] dark:text-white border-[#E5E7EB] dark:border-[#2A2A2A] hover:border-[#62397f]"
                                }`}
                        >
                            <Filter size={16} />
                            Filters
                            {hasActiveFilters && (
                                <span className="w-5 h-5 bg-white/20 rounded-full text-[10px] flex items-center justify-center font-bold">
                                    {Object.values(filters).filter(v => v !== "").length}
                                </span>
                            )}
                        </button>
                    </div>
                </div>

                {/* Filter Bar */}
                {showFilters && (
                    <div className="bg-white dark:bg-[#1A1A1A] p-5 rounded-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] shadow-sm animate-in fade-in slide-in-from-top-2 duration-200">
                        <div className="flex items-center gap-2 mb-4">
                            <Filter size={16} className="text-[#62397f]" />
                            <h3 className="text-sm font-bold text-[#111] dark:text-white">Filter Logs</h3>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
                            {/* Date From */}
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-[#6B7280] dark:text-[#A0A0A0] uppercase">From</label>
                                <input
                                    type="date"
                                    value={filters.date_from}
                                    onChange={(e) => handleFilterChange("date_from", e.target.value)}
                                    className={selectClass}
                                />
                            </div>
                            {/* Date To */}
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-[#6B7280] dark:text-[#A0A0A0] uppercase">To</label>
                                <input
                                    type="date"
                                    value={filters.date_to}
                                    onChange={(e) => handleFilterChange("date_to", e.target.value)}
                                    className={selectClass}
                                />
                            </div>
                            {/* Admin */}
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-[#6B7280] dark:text-[#A0A0A0] uppercase">Admin</label>
                                <select
                                    value={filters.admin_id}
                                    onChange={(e) => handleFilterChange("admin_id", e.target.value)}
                                    className={selectClass + " w-full"}
                                >
                                    <option value="">All Admins</option>
                                    {filterOptions.admins.map(a => (
                                        <option key={a.id} value={a.id}>{a.name}</option>
                                    ))}
                                </select>
                            </div>
                            {/* Action */}
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-[#6B7280] dark:text-[#A0A0A0] uppercase">Action</label>
                                <select
                                    value={filters.action}
                                    onChange={(e) => handleFilterChange("action", e.target.value)}
                                    className={selectClass + " w-full"}
                                >
                                    <option value="">All Actions</option>
                                    {filterOptions.actions.map(a => (
                                        <option key={a} value={a}>{a.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</option>
                                    ))}
                                </select>
                            </div>
                            {/* Entity Type */}
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-[#6B7280] dark:text-[#A0A0A0] uppercase">Entity</label>
                                <select
                                    value={filters.entity_type}
                                    onChange={(e) => handleFilterChange("entity_type", e.target.value)}
                                    className={selectClass + " w-full"}
                                >
                                    <option value="">All Entities</option>
                                    {filterOptions.entity_types.map(e => (
                                        <option key={e} value={e}>{e.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</option>
                                    ))}
                                </select>
                            </div>
                            {/* Search */}
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-[#6B7280] dark:text-[#A0A0A0] uppercase">Search</label>
                                <div className="relative">
                                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                                    <input
                                        type="text"
                                        placeholder="Entity ID..."
                                        value={filters.search}
                                        onChange={(e) => handleFilterChange("search", e.target.value)}
                                        className={selectClass + " w-full pl-9"}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Content */}
                {isLoading ? (
                    <AuditLogsSkeleton />
                ) : logs.length === 0 ? (
                    /* Empty State */
                    <div className="bg-white dark:bg-[#1A1A1A] rounded-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] p-16 flex flex-col items-center justify-center text-center">
                        <div className="w-16 h-16 bg-gray-100 dark:bg-[#262626] rounded-2xl flex items-center justify-center mb-4">
                            <ClipboardList size={28} className="text-[#9CA3AF]" />
                        </div>
                        <h3 className="text-lg font-bold text-[#111] dark:text-white mb-1">No activity recorded yet</h3>
                        <p className="text-sm text-[#6B7280] dark:text-[#A0A0A0] max-w-sm">
                            {hasActiveFilters
                                ? "No logs match your current filters. Try adjusting your search criteria."
                                : "System actions will appear here as administrators perform operations."
                            }
                        </p>
                    </div>
                ) : (
                    <>
                        {/* Log Table */}
                        <div className="bg-white dark:bg-[#1A1A1A] rounded-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] shadow-sm overflow-hidden">
                            {/* Desktop header */}
                            <div className="hidden md:grid grid-cols-[160px_130px_1fr_100px_80px_60px] gap-4 px-6 py-3.5 bg-[#F9FAFB] dark:bg-[#111] border-b border-[#E5E7EB] dark:border-[#2A2A2A]">
                                <span className="text-[10px] font-bold text-[#6B7280] dark:text-[#A0A0A0] uppercase tracking-wider">Timestamp</span>
                                <span className="text-[10px] font-bold text-[#6B7280] dark:text-[#A0A0A0] uppercase tracking-wider">Admin</span>
                                <span className="text-[10px] font-bold text-[#6B7280] dark:text-[#A0A0A0] uppercase tracking-wider">Action</span>
                                <span className="text-[10px] font-bold text-[#6B7280] dark:text-[#A0A0A0] uppercase tracking-wider">Entity</span>
                                <span className="text-[10px] font-bold text-[#6B7280] dark:text-[#A0A0A0] uppercase tracking-wider">Severity</span>
                                <span className="text-[10px] font-bold text-[#6B7280] dark:text-[#A0A0A0] uppercase tracking-wider"></span>
                            </div>

                            {/* Rows */}
                            {logs.map((log) => {
                                const sev = SEVERITY_CONFIG[log.severity] || SEVERITY_CONFIG.info;
                                return (
                                    <div key={log.id} className="group">
                                        {/* Desktop row */}
                                        <div className="hidden md:grid grid-cols-[160px_130px_1fr_100px_80px_60px] gap-4 px-6 py-4 border-b border-[#F3F4F6] dark:border-[#222] hover:bg-[#F9FAFB] dark:hover:bg-[#151515] transition-colors items-center">
                                            <span className="text-xs text-[#6B7280] dark:text-[#A0A0A0] flex items-center gap-1.5">
                                                <Clock size={12} className="flex-shrink-0" />
                                                {formatTimestamp(log.timestamp)}
                                            </span>
                                            <span className="text-sm font-medium text-[#111] dark:text-white truncate">{log.admin_name}</span>
                                            <span className="text-sm text-[#4B5563] dark:text-[#D1D5DB] truncate">{log.action_display}</span>
                                            <span className="text-xs text-[#6B7280] dark:text-[#A0A0A0] capitalize truncate">{log.entity_type?.replace("_", " ")}</span>
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border whitespace-nowrap ${sev.color}`}>
                                                {sev.label}
                                            </span>
                                            <button
                                                onClick={() => openDetail(log.id)}
                                                className="p-1.5 hover:bg-gray-100 dark:hover:bg-[#262626] rounded-lg transition-colors text-[#6B7280] hover:text-[#62397f]"
                                                title="View details"
                                            >
                                                <Eye size={16} />
                                            </button>
                                        </div>

                                        {/* Mobile card */}
                                        <div
                                            className="md:hidden p-4 border-b border-[#F3F4F6] dark:border-[#222] active:bg-[#F9FAFB] dark:active:bg-[#151515]"
                                            onClick={() => openDetail(log.id)}
                                        >
                                            <div className="flex items-start justify-between gap-3 mb-2">
                                                <p className="text-sm font-medium text-[#111] dark:text-white flex-1">{log.action_display}</p>
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border whitespace-nowrap flex-shrink-0 ${sev.color}`}>
                                                    {sev.label}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-3 text-xs text-[#6B7280] dark:text-[#A0A0A0]">
                                                <span>{log.admin_name}</span>
                                                <span>•</span>
                                                <span className="capitalize">{log.entity_type?.replace("_", " ")}</span>
                                                <span>•</span>
                                                <span>{formatTimestamp(log.timestamp)}</span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Pagination */}
                        {pagination.pages > 1 && (
                            <div className="flex items-center justify-between bg-white dark:bg-[#1A1A1A] px-6 py-4 rounded-2xl border border-[#E5E7EB] dark:border-[#2A2A2A]">
                                <p className="text-sm text-[#6B7280] dark:text-[#A0A0A0]">
                                    Page <span className="font-bold text-[#111] dark:text-white">{pagination.page}</span> of{" "}
                                    <span className="font-bold text-[#111] dark:text-white">{pagination.pages}</span>
                                    <span className="hidden sm:inline"> — {pagination.total} total entries</span>
                                </p>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                                        disabled={pagination.page <= 1}
                                        className="p-2 rounded-xl border border-[#E5E7EB] dark:border-[#333] hover:bg-gray-50 dark:hover:bg-[#262626] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                    >
                                        <ChevronLeft size={16} className="text-[#6B7280]" />
                                    </button>
                                    <button
                                        onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                                        disabled={pagination.page >= pagination.pages}
                                        className="p-2 rounded-xl border border-[#E5E7EB] dark:border-[#333] hover:bg-gray-50 dark:hover:bg-[#262626] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                    >
                                        <ChevronRight size={16} className="text-[#6B7280]" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Detail Modal */}
            {selectedLog && <LogDetailModal log={selectedLog} onClose={() => setSelectedLog(null)} />}

            {/* Loading overlay for detail fetch */}
            {isLoadingDetail && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
                    <div className="bg-white dark:bg-[#1A1A1A] p-6 rounded-2xl shadow-xl flex items-center gap-3">
                        <Loader2 size={20} className="animate-spin text-[#62397f]" />
                        <span className="text-sm font-medium text-[#111] dark:text-white">Loading log details…</span>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
}
